import React from "react";
import { Box, Chip, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import LGModalCrud from "../LGCrud/LGModalCrud";
import { useCompany } from "../../contexts/CompanyContext";
import PhysicalInventoryCountForm from "./PhysicalInventoryCountForm.tsx";
import PICUploadCsvModal from "./PICUploadCsvModal.tsx";
import PICReportModal from "./PICReportModal.tsx";
import {
  deletePhysicalInventoryCount,
  getPhysicalInventoryCountList,
  exportCSV,
  freezeStock,
  adjustStock,
} from "../../api/physicalInventoryCounts/index.ts";
import { useSnack } from "../../services/SnakbarProvider";
import { handleError } from "../../services/errorHandler";

const PhysicalInventoryCountTable = () => {
  const { selectedCompany } = useCompany();
  const navigate = useNavigate();
  const { openSnack } = useSnack();

  if (!selectedCompany) {
    return (
      <Box p={4}>
        <Typography>Seleccione una compañía primero</Typography>
      </Box>
    );
  }

  const setStatusColor = (statusCode) => {
    switch (statusCode) {
      case "PIC_PENDING":
        return "warning";
      case "PIC_COMPLETED":
        return "success";
      case "PIC_CANCELLED":
        return "error";
      case "PIC_FIXED":
        return "info";
      case "PIC_DRAFT":
        return "default";
      default:
        return "default";
    }
  };

  const handlePrintCSV = async (id: number) => {
    try {
      const response = await exportCSV(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `pic_export_${id}_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      openSnack("CSV descargado correctamente", "success");
    } catch (error) {
      openSnack(handleError(error), "error");
    }
  };

  const handleFreezeStock = async (id: number) => {
    try {
      await freezeStock(id);
      openSnack("Stock fijado correctamente", "success");
    } catch (error) {
      openSnack(handleError(error), "error");
    }
  };

  const handleAdjustStock = async (id: number) => {
    try {
      await adjustStock(id);
      openSnack("Existencias ajustadas correctamente", "success");
    } catch (error) {
      openSnack(handleError(error), "error");
    }
  };

  const buildDetailMenu = (row: any) => {
    const statusCode = row.status?.code;
    const menuItems: any[] = [
      {
        label: "Ver Proceso",
        header: "Conteo Físico",
        body: <Box />,
        onClick: (id: number) => navigate(`/physical-inventory-counts/dashboard/${id}`),
      },
    ];

    // Print CSV - always available
    menuItems.push({
      label: "Imprimir CSV",
      header: "Descargar CSV",
      body: <Box />,
      onClick: (id: number) => handlePrintCSV(id),
    });

    // Fijar Inventario - only if status is PIC_DRAFT
    if (statusCode === "PIC_DRAFT") {
      menuItems.push({
        label: "Fijar Inventario",
        header: "Fijar Inventario",
        body: <Box />,
        onClick: (id: number) => handleFreezeStock(id),
      });
    }

    // Subir datos - show modal
    menuItems.push({
      label: "Subir datos",
      header: "Subir Datos CSV",
      body: (
        <PICUploadCsvModal
          picId={row.id}
          onSuccess={() => {
            // Refresh the table
            window.location.reload();
          }}
        />
      ),
    });

    // Ver reporte - show modal
    menuItems.push({
      label: "Ver reporte",
      header: "Reporte de Conteo Físico",
      body: <PICReportModal picId={row.id} />,
    });

    // Ajustar existencias
    menuItems.push({
      label: "Ajustar existencias",
      header: "Ajustar Existencias",
      body: <Box />,
      onClick: (id: number) => handleAdjustStock(id),
    });

    return menuItems;
  };

  const physicalInventoryCountCrudProps = {
    fetchFunction: getPhysicalInventoryCountList,
    deleteFunction: deletePhysicalInventoryCount,
    data: { companyId: selectedCompany.id },
    detailMenu: buildDetailMenu,
    columns: [
      {
        field: "code",
        headerName: "Código",
        flex: 0.2,
      },
      {
        field: "warehouse",
        headerName: "Almacén",
        flex: 0.2,
        renderCell: (params) => (
          <Typography>{params.row.warehouse?.name || "-"}</Typography>
        ),
      },
      {
        field: "status",
        headerName: "Estatus",
        flex: 0.15,
        renderCell: (params) => (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <Chip
              size="small"
              label={params.row.status?.name || "-"}
              color={setStatusColor(params.row.status?.code)}
            />
          </Box>
        ),
      },
    ],
    addModalData: {
      header: "Nuevo Conteo Físico",
      body: <PhysicalInventoryCountForm onSubmit={() => { }} />,
    },
    editModalData: {
      header: "Editar Conteo Físico",
      body: <PhysicalInventoryCountForm onSubmit={() => { }} />,
    },
    actions: ["create", "delete", "detail"],
    customButtons: [],
  };

  return <LGModalCrud props={physicalInventoryCountCrudProps} />;
};

export default PhysicalInventoryCountTable;
